import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { pages } from "../../constants/PagePaths";
import { FreeUseDay } from "../../constants/General";
import { logEvent } from "../../services/eventLog";
import styles from "./welcomePage.module.scss";

/**
 * 가입 직후 시작 화면 (전환율 2단계, 10-08).
 * "무엇을 하러 오셨나요?" 세 갈래를 고르면 바로 그 화면으로 보낸다. 고른 값은 event_log onboarding_choice 로 남는다.
 * - design     : 수의계약 설계내역 → 프로젝트 목록 + 튜토리얼 선택 열기
 * - excel_work : 받은 내역서 엑셀 정리(공무) → 엑셀 자동화
 * - drawing    : 도면 적산 기대 → 자동 적산은 하지 않는다고 솔직히 안내하고 설계 의뢰/직접 만들기로
 * - skip       : 나중에
 * 프로젝트를 자동으로 만들지 않는다(만료 후 '프로젝트 0개면 1개 생성' 규칙과 얽힘).
 */
/** 프로젝트 목록에 도착하면 튜토리얼 선택을 바로 펼치게 하는 1회용 표시 (CostProjectsPage 가 읽고 지움). */
export const OPEN_TUTORIAL_KEY = "conax_open_tutorial";
const markOpenTutorial = () => {
  try {
    window.sessionStorage.setItem(OPEN_TUTORIAL_KEY, "1");
  } catch (e) {
    // 저장이 막혀도 이동은 한다
  }
};

export const WELCOME_CHOICES = ["design", "excel_work", "drawing", "skip"] as const;
export type WelcomeChoice = typeof WELCOME_CHOICES[number];

export const choiceTarget = (choice: WelcomeChoice): string | null => {
  switch (choice) {
    case "design":
      return `${pages.costprojects}/normal`;
    case "excel_work":
      return pages.excel;
    case "skip":
      return pages.index;
    default:
      return null; // drawing 은 안내를 먼저 보여 준다
  }
};

const WelcomePage = () => {
  const history = useHistory();
  const [showDrawing, setShowDrawing] = useState(false);

  const choose = (choice: WelcomeChoice) => {
    logEvent("onboarding_choice", choice);
    if (choice === "design") markOpenTutorial();
    const target = choiceTarget(choice);
    if (target) history.replace(target);
    else setShowDrawing(true);
  };

  return (
    <div className={styles.wrap}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>시작하기 | 콘엑스</title>
      </Helmet>
      <div className={styles.inner}>
        <div className={styles.badge}>무료 체험 {FreeUseDay}일</div>
        <h1 className={styles.title}>가입을 환영해요. 무엇을 하러 오셨나요?</h1>
        <p className={styles.sub}>고르시면 바로 그 화면으로 안내해 드릴게요. 체험 기간 동안 모든 기능을 쓰실 수 있어요.</p>

        {!showDrawing ? (
          <div className={styles.cards}>
            <button type="button" className={styles.card} onClick={() => choose("design")} data-choice="design">
              <span className={`material-symbols-outlined ${styles.icon}`}>edit_document</span>
              <strong>수의계약 설계내역을 만들러 왔어요</strong>
              <span>품셈과 단가가 들어간 설계내역서를 직접 만들어요. 튜토리얼로 바로 시작할 수 있어요.</span>
            </button>
            <button type="button" className={styles.card} onClick={() => choose("excel_work")} data-choice="excel_work">
              <span className={`material-symbols-outlined ${styles.icon}`}>table_view</span>
              <strong>받은 내역서 엑셀을 정리하러 왔어요</strong>
              <span>공무 업무용이에요. 다른 곳에서 받은 내역서 엑셀을 올리면 단가 맞추기·나누기·갑지 정리를 자동으로 해요.</span>
            </button>
            <button type="button" className={styles.card} onClick={() => choose("drawing")} data-choice="drawing">
              <span className={`material-symbols-outlined ${styles.icon}`}>architecture</span>
              <strong>도면을 넣으면 적산되는 줄 알고 왔어요</strong>
              <span>도면으로 무엇을 할 수 있는지 먼저 알려 드릴게요.</span>
            </button>
          </div>
        ) : (
          <div className={styles.drawing}>
            <p>
              콘엑스는 도면을 읽어서 물량을 자동으로 뽑아 주지는 않아요.
              <br />
              대신 아래 두 가지 중에 고르실 수 있어요.
            </p>
            <div className={styles.drawingActions}>
              <button type="button" className="btn btn-primary" onClick={() => history.replace(pages.requestapplication)}>
                도면을 보내고 설계내역을 맡기기
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => {
                  markOpenTutorial();
                  history.replace(`${pages.costprojects}/normal`);
                }}
              >
                물량은 제가 알아요, 직접 만들어 볼게요
              </button>
            </div>
          </div>
        )}

        <button type="button" className={styles.skip} onClick={() => choose("skip")} data-choice="skip">
          나중에 고를게요
        </button>
      </div>
    </div>
  );
};

export default WelcomePage;
